import { useCallback, useMemo, useState } from 'react';
import { Upload, Scissors, Loader2, Info, Download, Video } from 'lucide-react';
import { detectNonSilentSegments } from './AudioTrimSilenceTool';

const DEFAULT_THRESHOLD = 0.02;
const DEFAULT_MIN_SILENCE_MS = 400;

type WindowWithWebkitAudioContext = Window &
  typeof globalThis & {
    webkitAudioContext?: typeof AudioContext;
  };

let ffmpegInstance: import('@ffmpeg/ffmpeg').FFmpeg | null = null;

async function getFfmpeg() {
  if (ffmpegInstance) return ffmpegInstance;
  const { FFmpeg } = await import('@ffmpeg/ffmpeg');
  ffmpegInstance = new FFmpeg();
  return ffmpegInstance;
}

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if ('AudioContext' in window) {
    return new AudioContext();
  }
  const windowWithWebkit = window as WindowWithWebkitAudioContext;
  if (windowWithWebkit.webkitAudioContext) {
    return new windowWithWebkit.webkitAudioContext();
  }
  return null;
}

export function VideoTrimSilenceTool() {
  const [file, setFile] = useState<File | null>(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [originalDuration, setOriginalDuration] = useState<number | null>(null);
  const [trimmedDuration, setTrimmedDuration] = useState<number | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [silenceThreshold, setSilenceThreshold] = useState(DEFAULT_THRESHOLD);
  const [minSilenceMs, setMinSilenceMs] = useState<number>(
    DEFAULT_MIN_SILENCE_MS
  );
  const [minSilenceMsInput, setMinSilenceMsInput] = useState<string>(
    String(DEFAULT_MIN_SILENCE_MS)
  );
  const [loadingCore, setLoadingCore] = useState(false);

  const thresholdLabel = useMemo(() => {
    if (silenceThreshold <= 0.01) return '非常敏感（容易誤判雜訊為說話）';
    if (silenceThreshold <= 0.03) return '建議值（一般人聲）';
    return '較嚴格（只保留較大聲片段）';
  }, [silenceThreshold]);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0] ?? null;
      setFile(f);
      setError(null);
      setOriginalDuration(null);
      setTrimmedDuration(null);
      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
        setDownloadUrl(null);
      }
      setMinSilenceMs(DEFAULT_MIN_SILENCE_MS);
      setMinSilenceMsInput(String(DEFAULT_MIN_SILENCE_MS));
    },
    [downloadUrl]
  );

  const handleProcess = useCallback(async () => {
    if (!file) {
      setError('請先選擇 MP4 影片');
      return;
    }

    const ctx = getAudioContext();
    if (!ctx) {
      setError(
        '此瀏覽器不支援 Web Audio API，請改用最新版本 Chrome / Edge / Safari。'
      );
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const fileData = await file.arrayBuffer();
      const audioBuffer = await ctx.decodeAudioData(fileData.slice(0));

      setOriginalDuration(audioBuffer.duration);

      const segments = detectNonSilentSegments(audioBuffer, {
        threshold: silenceThreshold,
        minSilenceMs,
      });

      if (segments.length === 0) {
        setError(
          '偵測不到明顯有聲音的區段，請嘗試調低靜音門檻或確認影片內容。'
        );
        return;
      }

      const times = segments.map(seg => ({
        start: seg.startSample / audioBuffer.sampleRate,
        end: seg.endSample / audioBuffer.sampleRate,
      }));

      const totalDuration = times.reduce((sum, t) => {
        const len = Math.max(0, t.end - t.start);
        return sum + len;
      }, 0);
      setTrimmedDuration(totalDuration);

      setLoadingCore(true);
      const ffmpeg = await getFfmpeg();

      const { fetchFile, toBlobURL } = await import('@ffmpeg/util');
      const baseURL =
        'https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.10/dist/esm';

      if (!ffmpeg.loaded) {
        await ffmpeg.load({
          coreURL: await toBlobURL(
            `${baseURL}/ffmpeg-core.js`,
            'text/javascript'
          ),
          wasmURL: await toBlobURL(
            `${baseURL}/ffmpeg-core.wasm`,
            'application/wasm'
          ),
        });
      }
      setLoadingCore(false);

      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
        setDownloadUrl(null);
      }

      await ffmpeg.writeFile('input.mp4', await fetchFile(file));

      const betweenExpr = times
        .map(t => `between(t,${t.start.toFixed(3)},${t.end.toFixed(3)})`)
        .join('+');

      const vf = `select='${betweenExpr}',setpts=N/FRAME_RATE/TB`;
      const af = `aselect='${betweenExpr}',asetpts=N/SR/TB`;

      await ffmpeg.exec([
        '-i',
        'input.mp4',
        '-vf',
        vf,
        '-af',
        af,
        '-movflags',
        'faststart',
        'output.mp4',
      ]);

      const data = await ffmpeg.readFile('output.mp4');
      if (!(data instanceof Uint8Array)) {
        throw new Error('影片輸出格式異常，請重新嘗試。');
      }
      const mp4Buffer = new ArrayBuffer(data.byteLength);
      new Uint8Array(mp4Buffer).set(data);
      const blob = new Blob([mp4Buffer], { type: 'video/mp4' });
      const url = URL.createObjectURL(blob);
      setDownloadUrl(url);
    } catch (e) {
      const msg =
        e instanceof Error ? e.message : '處理影片時發生錯誤，請稍後再試。';
      setError(msg);
    } finally {
      setProcessing(false);
      void ctx.close();
    }
  }, [file, silenceThreshold, minSilenceMs, downloadUrl]);

  const formatDuration = (seconds: number | null) => {
    if (seconds == null || Number.isNaN(seconds)) return '—';
    const s = Math.max(0, seconds);
    const m = Math.floor(s / 60);
    const sec = Math.round(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const isBusy = processing || loadingCore;

  return (
    <>
      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <div className="mb-4 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
          <Video className="h-4 w-4 text-primary" />
          <p>
            上傳 MP4
            影片，工具會依音訊偵測長時間靜音並將整段影片剪掉，保留有說話的片段，音畫同步。
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              選擇 MP4 影片
            </label>
            <div className="flex flex-wrap items-center gap-3">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90">
                <Upload className="h-4 w-4" />
                <span>選擇檔案</span>
                <input
                  type="file"
                  accept="video/mp4,video/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </label>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {file
                  ? `${file.name}（${(file.size / 1024 / 1024).toFixed(2)} MB）`
                  : '尚未選擇檔案'}
              </span>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
                靜音門檻（振幅）
              </label>
              <input
                type="range"
                min={0.005}
                max={0.08}
                step={0.005}
                value={silenceThreshold}
                onChange={e => setSilenceThreshold(parseFloat(e.target.value))}
                className="w-full"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                目前：{silenceThreshold.toFixed(3)}（{thresholdLabel}）
              </p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
                視為靜音的最短時間（毫秒）
              </label>
              <input
                type="number"
                min={100}
                max={2000}
                step={50}
                value={minSilenceMsInput}
                onChange={e => setMinSilenceMsInput(e.target.value)}
                onBlur={e => {
                  const raw = e.target.value.trim();
                  const parsed = Number(raw);
                  const next = Math.min(
                    2000,
                    Math.max(
                      100,
                      Number.isFinite(parsed) ? parsed : DEFAULT_MIN_SILENCE_MS
                    )
                  );
                  setMinSilenceMs(next);
                  setMinSilenceMsInput(String(next));
                }}
                className="w-24 rounded border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                例如 400 代表連續 0.4 秒幾乎沒聲音才會被視為靜音。
              </p>
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}

          <button
            type="button"
            onClick={handleProcess}
            disabled={!file || isBusy}
            className="inline-flex items-center gap-2 rounded bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
          >
            {isBusy ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {loadingCore ? '載入影片處理引擎中…' : '處理中…'}
              </>
            ) : (
              <>
                <Scissors className="h-4 w-4" />
                去除靜音並產生新影片
              </>
            )}
          </button>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm dark:border-gray-700 dark:bg-gray-800">
              <p className="mb-1 font-medium text-gray-700 dark:text-gray-200">
                長度比較（依音訊推估）
              </p>
              <p className="text-gray-600 dark:text-gray-300">
                原始：{formatDuration(originalDuration)}
              </p>
              <p className="text-gray-600 dark:text-gray-300">
                去靜音後：{formatDuration(trimmedDuration)}
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm dark:border-gray-700 dark:bg-gray-800">
              <p className="mb-1 font-medium text-gray-700 dark:text-gray-200">
                下載
              </p>
              {downloadUrl ? (
                <a
                  href={downloadUrl}
                  download={
                    file
                      ? file.name.replace(/\.[^.]+$/, '') + '-trimmed.mp4'
                      : 'trimmed-video.mp4'
                  }
                  className="inline-flex items-center gap-2 rounded bg-gray-700 px-3 py-2 text-xs font-medium text-white hover:bg-gray-600 dark:bg-gray-600 dark:hover:bg-gray-500"
                >
                  <Download className="h-4 w-4" />
                  下載去靜音影片（MP4）
                </a>
              ) : (
                <p className="text-gray-500 dark:text-gray-400">
                  產生完成後，會在這裡提供下載連結。
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <section className="rounded-lg border border-gray-200 bg-white p-6 text-sm leading-relaxed text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
        <div className="mb-2 flex items-center gap-2">
          <Info className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            使用說明與限制
          </h2>
        </div>
        <ul className="list-inside list-disc space-y-1">
          <li>
            處理完全在瀏覽器進行，不會上傳影片到伺服器，但需要較多記憶體與
            CPU，建議在桌機或筆電上使用。
          </li>
          <li>
            影片越長、解析度越高，處理時間越久；若失敗可嘗試先壓縮解析度或切成多段處理。
          </li>
          <li>
            工具會依音訊偵測靜音時間，將同一時間軸上的畫面與聲音一併剪掉，以維持口型同步。
          </li>
        </ul>
      </section>
    </>
  );
}
